import React, { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { AlertTriangle, CalendarRange, ClipboardList, Sparkles } from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Page,
  Select,
  type DataTableColumn,
} from '@/components/ui'
import { usePermissions } from '@/hooks/usePermissions'
import {
  useCreatePlan,
  useGenerateAllocation,
  usePlanCapacity,
  usePlanSubjects,
  usePlanningDashboard,
  usePlanningMatrix,
  usePlans,
  transitionPlan,
  type MatrixRow,
} from '@/services/acdmcPlanningService'
import { errorMessage } from '@/components/acdmc/AcdmcUi'
import '@/components/acdmc/acdmc.css'

const STATUS_TONE: Record<string, 'success' | 'warning' | 'neutral'> = {
  published: 'success',
  draft: 'neutral',
  submitted: 'warning',
  under_review: 'warning',
  approved: 'success',
  archived: 'neutral',
}

const AcdmcPlanning: React.FC = () => {
  const { can } = usePermissions()
  const canWrite = can('acdmc.planning.write')
  const { data: dashboard } = usePlanningDashboard()
  const { data: plans = [] } = usePlans()
  const { data: subjects = [] } = usePlanSubjects()
  const [selectedPlanId, setSelectedPlanId] = useState('')
  const { data: matrixData, isLoading: matrixLoading } = usePlanningMatrix(selectedPlanId || null)
  const createPlan = useCreatePlan()
  const capacity = usePlanCapacity(selectedPlanId)
  const generate = useGenerateAllocation(selectedPlanId)
  const [newSubjectId, setNewSubjectId] = useState('')

  const matrixColumns = useMemo(() => {
    const months = matrixData?.months || []
    const cols: DataTableColumn<MatrixRow>[] = [
      { key: 'topic', header: 'Chapter / topic', mobile: 'title', render: (row) => row.title },
      {
        key: 'estimated',
        header: 'Est. periods',
        numeric: true,
        align: 'right',
        render: (row) => row.estimatedPeriods ?? '—',
      },
      ...months.map(
        (m): DataTableColumn<MatrixRow> => ({
          key: m,
          header: m,
          numeric: true,
          align: 'right',
          render: (row) => row.byMonth[m] ?? 0,
        }),
      ),
      {
        key: 'total',
        header: 'Planned',
        numeric: true,
        align: 'right',
        render: (row) => row.totalPlanned,
      },
      {
        key: 'status',
        header: 'Status',
        render: (row) => (
          <Badge tone={row.status === 'planned' ? 'success' : 'warning'}>
            {row.status === 'planned' ? 'On track' : 'Short'}
          </Badge>
        ),
      },
    ]
    return cols
  }, [matrixData?.months])

  const handleCreatePlan = async () => {
    if (!newSubjectId) {
      toast.error('Choose a subject with a published syllabus.')
      return
    }
    try {
      const res = await createPlan.mutateAsync({ subjectConfigId: newSubjectId, allocationStrategy: 'balanced' })
      const id = res?.data?._id
      toast.success('Draft annual plan created')
      if (id) setSelectedPlanId(id)
      setNewSubjectId('')
    } catch (err) {
      toast.error(errorMessage(err, 'Could not create plan'))
    }
  }

  const selectedPlan = plans.find((p) => p._id === selectedPlanId)

  return (
    <Page
      title="Academic planning"
      description="Turn published school syllabi into period-wise annual plans using timetable capacity, the school calendar and examination schedules."
    >
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">Session</div>
          <div className="text-lg font-semibold">{dashboard?.academicSession || '—'}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">Active plans</div>
          <div className="text-lg font-semibold">{dashboard?.totalPlans ?? 0}</div>
          <div className="text-xs text-muted-foreground">{dashboard?.published ?? 0} published</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">In review</div>
          <div className="text-lg font-semibold">{(dashboard?.pending ?? 0) + (dashboard?.draft ?? 0)}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">Capacity alerts</div>
          <div className="text-lg font-semibold">{dashboard?.deficitSubjects ?? 0}</div>
        </Card>
      </div>

      {(dashboard?.alerts?.length ?? 0) > 0 && (
        <Card className="p-4 border-amber-200 dark:border-amber-900">
          <div className="flex items-center gap-2 font-medium text-amber-900 dark:text-amber-100">
            <AlertTriangle className="h-4 w-4" />
            Needs attention
          </div>
          <ul className="mt-2 space-y-1 text-sm">
            {dashboard!.alerts.map((a) => (
              <li key={a.planId}>
                <button type="button" className="text-left underline-offset-2 hover:underline" onClick={() => setSelectedPlanId(a.planId)}>
                  {a.message || `Capacity gap ${a.gap}`}
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {canWrite && (
        <Card className="p-4 space-y-3">
          <div className="flex items-center gap-2 font-medium">
            <Sparkles className="h-4 w-4" />
            New annual plan
          </div>
          <p className="text-sm text-muted-foreground">
            Only subjects with a published school syllabus appear here. Timetable (TMTBL) must exist before capacity runs.
          </p>
          <div className="flex flex-wrap gap-3 items-end">
            <Select
              label="Subject"
              value={newSubjectId}
              onChange={(e) => setNewSubjectId(e.target.value)}
              options={[
                { value: '', label: 'Select subject…' },
                ...subjects.map((s) => ({
                  value: s._id,
                  label: `${s.capabbleClassKey} · ${s.subjectName}`,
                })),
              ]}
            />
            <Button onClick={handleCreatePlan} loading={createPlan.isPending}>
              Create draft plan
            </Button>
          </div>
        </Card>
      )}

      <Card className="p-4">
        <div className="flex items-center gap-2 font-medium mb-2">
          <ClipboardList className="h-4 w-4" />
          Session plans
        </div>
        {plans.length === 0 ? (
          <EmptyState title="No plans yet" body="Create a draft plan for each grade-subject you want to schedule this session." />
        ) : (
          <ul className="space-y-2">
            {plans.map((p) => (
              <li key={p._id} className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-2">
                <button type="button" className="text-left" onClick={() => setSelectedPlanId(p._id)}>
                  <div className="font-medium">
                    {p.capabbleClassKey}
                    {p.section ? ` ${p.section}` : ''} · {p.subjectName}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Rev {p.revisionNumber} · {p.status} · {p.allocationStrategy}
                  </div>
                </button>
                <Badge tone={STATUS_TONE[p.status] || 'neutral'}>{p.status}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {selectedPlanId && (
        <Card className="p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 font-medium">
              <CalendarRange className="h-4 w-4" />
              Annual planning matrix
            </div>
            {canWrite && selectedPlan && selectedPlan.status === 'draft' && (
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" size="sm" loading={capacity.isPending} onClick={() => capacity.mutateAsync().catch((e) => toast.error(errorMessage(e, 'Capacity failed')))}>
                  Calculate capacity
                </Button>
                <Button size="sm" loading={generate.isPending} onClick={() => generate.mutateAsync(undefined).catch((e) => toast.error(errorMessage(e, 'Allocation failed')))}>
                  Suggest allocation
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    transitionPlan(selectedPlanId, 'submitted')
                      .then(() => toast.success('Submitted for review'))
                      .catch((e) => toast.error(errorMessage(e, 'Submit failed')))
                  }
                >
                  Submit for review
                </Button>
              </div>
            )}
          </div>

          {matrixLoading && <p className="text-sm text-muted-foreground">Loading matrix…</p>}

          {matrixData?.capacity && (
            <div className="rounded-md bg-muted/50 p-3 text-sm space-y-1">
              <div>
                Available {matrixData.capacity.effectiveAvailablePeriods} · Required {matrixData.capacity.totalRequiredPeriods} · Gap{' '}
                <span className={matrixData.capacity.capacityGap < 0 ? 'text-amber-600 font-medium' : ''}>
                  {matrixData.capacity.capacityGap}
                </span>
              </div>
              <p className="text-muted-foreground">{matrixData.capacity.explanation}</p>
              <p className="text-xs text-muted-foreground">
                Estimated completion {matrixData.capacity.projectedCompletionDate} — not a guarantee.
              </p>
            </div>
          )}

          {matrixData && (
            <>
              <div className="md:hidden space-y-3">
                {matrixData.matrix.map((row) => (
                  <Card key={row.topicId} className="p-3 text-sm">
                    <div className="font-medium">{row.title}</div>
                    <div className="text-muted-foreground">
                      Est. {row.estimatedPeriods ?? '—'} · Planned {row.totalPlanned}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {matrixData.months.map((m) =>
                        row.byMonth[m] ? (
                          <Badge key={m} tone="neutral">
                            {m}: {row.byMonth[m]}
                          </Badge>
                        ) : null,
                      )}
                    </div>
                  </Card>
                ))}
              </div>
              <div className="hidden md:block overflow-x-auto">
                <DataTable columns={matrixColumns} rows={matrixData.matrix} getRowKey={(r) => r.topicId} empty={<EmptyState title="No allocation yet" body="Calculate capacity, then suggest an allocation to fill the matrix." compact />} />
              </div>
            </>
          )}
        </Card>
      )}
    </Page>
  )
}

export default AcdmcPlanning
